import React, {useEffect, useMemo, useRef, useState} from "react";
import {Button} from "primereact/button";
import {Skeleton} from "primereact/skeleton";
import Avatar from "../../../../system/components/avatar";
import {scheduleAssistantApi} from "../../../../models/task/schedule-assistant-api";
import {DURATIONS, addDays, barSegment, clock, dayLabel, digits, minutesText, relativeDay, tehranDayOf, toSeconds} from "./schedule-assistant-labels";
import "./schedule-assistant.css";

// «دستیار برنامه‌ریزی کار» (boss's task «زمانبندی کار تغییر کنه», 2026-10-31; spec «سند زمانبندی کار»;
// docs/tasks/35-schedule-assistant-progress-chart.md). Shows the assignee's day, the tasks the chosen range overlaps
// (all of them) and free slots of the needed length. Choosing a slot only fills the form (onPick) — it never saves.
// Times are unix seconds, as in the task form and the date picker.
export default function ScheduleAssistant({assignee, start, end, excludeTaskKey, onPick, onConflictsChange, saveLabel = "ایجاد کار", compact = false}) {
    const assigneeKey = assignee?.documentId || null;
    const hasRange = Number.isFinite(start) && Number.isFinite(end) && end > start;
    const [duration, setDuration] = useState(60);
    // the day browsed with the arrows belongs to the date it was browsed from; a new date in the picker or the form
    // brings the assistant to that date at once (boss's report 2026-11-01: choosing «امروز» / «فردا» left the old day)
    const [browsed, setBrowsed] = useState({anchor: null, day: null});
    const [days, setDays] = useState(7);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const [retry, setRetry] = useState(0);
    const requestRef = useRef(0);
    const today = tehranDayOf(Math.floor(Date.now() / 1000));
    // the chosen date: the start, or the due date alone when only that is set (a quick «امروز» / «فردا» sets just it)
    const anchor = Number.isFinite(start) ? start : Number.isFinite(end) ? end : null;
    const anchorKey = `${start ?? ""}|${end ?? ""}`;
    const viewDay = browsed.anchor === anchorKey ? browsed.day : null;
    const day = viewDay || (anchor !== null ? tehranDayOf(anchor) : today);
    const setViewDay = (nextDay) => setBrowsed({anchor: anchorKey, day: nextDay});
    const rangeDuration = hasRange ? Math.round((end - start) / 60) : null;

    useEffect(() => { setDays(7); }, [assigneeKey, start, end, duration]);

    useEffect(() => {
        if (!assigneeKey) {
            setResult(null);
            return undefined;
        }
        const requestId = ++requestRef.current;
        setLoading(true);
        setFailed(false);
        const timer = setTimeout(() => {
            scheduleAssistantApi.get({
                assignee: assigneeKey,
                ...(hasRange ? {start: start * 1000, end: end * 1000} : {duration}),
                day,
                days,
                ...(excludeTaskKey ? {exclude: excludeTaskKey} : {}),
            }).then((data) => {
                if (requestId !== requestRef.current) return;
                setResult(data);
                onConflictsChange?.((data?.conflicts || []).map((item) => item.key || `${item.start}|${item.end}`));
            }).catch(() => {
                if (requestId === requestRef.current) setFailed(true);
            }).finally(() => {
                if (requestId === requestRef.current) setLoading(false);
            });
        }, 250);
        return () => clearTimeout(timer);
        // onConflictsChange is a callback of the parent; it does not change what is asked
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assigneeKey, hasRange, start, end, duration, day, days, excludeTaskKey, retry]);

    const pickDuration = (minutes) => {
        setDuration(minutes);
        // with a start chosen, the duration keeps the start and moves the end
        if (Number.isFinite(start)) onPick?.({start, end: start + minutes * 60});
    };

    const conflicts = result?.conflicts || [];
    const suggestions = result?.suggestions || [];
    const shownDuration = rangeDuration ?? duration;

    return <aside className={`sa-panel flex flex-column ${compact ? "sa-compact" : ""}`} aria-label="دستیار برنامه‌ریزی کار">
        <header className="sa-header flex align-items-center gap-2 p-3">
            <span className="sa-icon flex align-items-center justify-content-center flex-shrink-0"><i className="pi pi-calendar-clock" aria-hidden="true"/></span>
            <span className="flex-1 min-w-0">
                <strong className="block text-sm text-900">دستیار برنامه‌ریزی کار</strong>
                <small className="block text-2xs text-500 mt-1">برنامهٔ مسئول، تداخل‌ها و زمان‌های آزاد</small>
            </span>
        </header>
        <div className="sa-body flex-1 overflow-auto flex flex-column gap-3 p-3">
            {!assigneeKey ? <div className="sa-hint text-xs line-height-3 p-3 border-round-lg">
                <i className="pi pi-user ml-2" aria-hidden="true"/>برای دیدن برنامهٔ زمانی و پیشنهاد زمان آزاد، اول <strong>مسئول کار</strong> را انتخاب کنید.
            </div> : <>
                <section className="sa-card p-3 border-round-xl">
                    <div className="flex align-items-center gap-2">
                        {assignee?.member && <Avatar memberData={assignee.member} className="text-2xs flex-shrink-0" style={{width: "2rem", height: "2rem"}}/>}
                        <span className="flex-1 min-w-0">
                            <strong className="block text-sm text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">{result?.assignee?.name || assignee?.member?.fullname || "مسئول کار"}</strong>
                            <small className="block text-2xs text-500">برنامهٔ ثبت‌شدهٔ مسئول</small>
                        </span>
                        <span className="sa-day flex align-items-center gap-1 flex-shrink-0">
                            <button type="button" className="sa-day-nav" aria-label="روز قبل" onClick={() => setViewDay(addDays(day, -1))}><i className="pi pi-chevron-right text-2xs"/></button>
                            <span className="text-2xs">{relativeDay(day, today)}</span>
                            <button type="button" className="sa-day-nav" aria-label="روز بعد" onClick={() => setViewDay(addDays(day, 1))}><i className="pi pi-chevron-left text-2xs"/></button>
                        </span>
                    </div>
                    <DayBar result={result} day={day} selection={hasRange ? {start, end} : null} loading={loading && !result}/>
                </section>

                <section className="sa-card p-3 border-round-xl">
                    <div className="flex align-items-center justify-content-between gap-2 mb-2">
                        <span className="text-xs font-bold text-900">مدت زمان کار</span>
                        {shownDuration ? <span className="text-2xs text-primary font-bold">{minutesText(shownDuration)}</span> : null}
                    </div>
                    <div className="sa-durations flex gap-1" role="group" aria-label="مدت زمان کار">
                        {DURATIONS.map((minutes) => <button key={minutes} type="button" className={`sa-duration flex-1 ${shownDuration === minutes ? "active" : ""}`} aria-pressed={shownDuration === minutes} onClick={() => pickDuration(minutes)}>{digits(minutes)} د</button>)}
                    </div>
                    {!hasRange && <small className="block text-2xs text-500 mt-2 line-height-3">زمان شروع و پایان هنوز انتخاب نشده؛ پیشنهادها با همین مدت ساخته می‌شوند.</small>}
                </section>

                {result?.longTasks?.length > 0 && <section className="sa-card p-3 border-round-xl">
                    <strong className="block text-xs text-900 mb-1">کارهای چندروزهٔ هم‌زمان</strong>
                    <small className="block text-2xs text-500 line-height-3 mb-2">این کارها چند روز طول می‌کشند؛ در برنامهٔ ساعتی و تداخل حساب نمی‌شوند.</small>
                    {result.longTasks.map((item, index) => <div key={item.key || index} className="flex align-items-center justify-content-between gap-2 text-2xs py-1">
                        <span className="text-700 white-space-nowrap overflow-hidden text-overflow-ellipsis">{item.title || "کار بدون دسترسی به جزئیات"}</span>
                        <span className="text-500 flex-shrink-0">تا {dayLabel(tehranDayOf(toSeconds(item.end)))}</span>
                    </div>)}
                </section>}

                {failed ? <div className="sa-error flex align-items-center justify-content-between gap-2 p-3 border-round-lg text-xs">
                    <span>برنامهٔ مسئول بارگذاری نشد.</span>
                    <Button type="button" text size="small" className="text-xs p-1" label="تلاش دوباره" onClick={() => setRetry((value) => value + 1)}/>
                </div> : loading && !result ? <Skeleton height="6rem" className="border-round-xl"/> : result && <>
                    {hasRange && (conflicts.length ? <section className="sa-conflict p-3 border-round-xl" aria-live="polite">
                        <div className="flex align-items-start gap-2">
                            <i className="pi pi-exclamation-triangle text-sm mt-1" aria-hidden="true"/>
                            <span className="flex-1">
                                <strong className="block text-xs">تداخل زمانی با {digits(conflicts.length)} کار ثبت‌شده</strong>
                                <small className="block text-2xs mt-1 line-height-3">بازهٔ انتخابی ({clock(start)} تا {clock(end)}) با برنامهٔ فعال {result.assignee?.isMe ? "شما" : result.assignee?.name || "مسئول"} هم‌پوشانی دارد.</small>
                            </span>
                        </div>
                        <div className="flex flex-column gap-2 mt-2">
                            {conflicts.map((item, index) => <div key={item.key || index} className="sa-conflict-item flex align-items-center gap-2 p-2 border-round-lg">
                                <span className="flex-1 min-w-0">
                                    <strong className="block text-xs text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">{item.title || "بازهٔ مشغول (بدون دسترسی به جزئیات)"}</strong>
                                    <small className="block text-2xs text-500 mt-1">{clock(item.start)} تا {clock(item.end)}{item.project ? ` · ${item.project}` : ""}</small>
                                </span>
                                <span className="sa-tag danger flex-shrink-0">{item.full ? "تداخل کامل" : `تداخل ${digits(item.overlapMinutes)} د`}</span>
                            </div>)}
                        </div>
                    </section> : <div className="sa-ok flex align-items-center gap-2 p-2 px-3 border-round-lg text-xs" aria-live="polite">
                        <i className="pi pi-check-circle" aria-hidden="true"/> بدون تداخل ثبت‌شده در این بازه
                    </div>)}

                    {(!hasRange || conflicts.length > 0) && <section className="flex flex-column gap-2">
                        <div className="flex align-items-center justify-content-between">
                            <strong className="text-xs text-900"><i className="pi pi-sparkles text-primary ml-1" aria-hidden="true"/>پیشنهادهای زمان آزاد</strong>
                            {suggestions.length > 0 && <small className="text-2xs text-500">{digits(suggestions.length)} بازهٔ آزاد</small>}
                        </div>
                        {result.noneBeforeDeadline && <small className="sa-warn text-2xs p-2 border-round-lg">پیش از مهلت کار، زمان آزادی با این مدت پیدا نشد.</small>}
                        {suggestions.length ? suggestions.map((slot, index) => <button key={slot.start} type="button" className={`sa-slot flex align-items-center gap-2 w-full text-right p-2 px-3 border-round-xl ${index === 0 ? "best" : ""}`} onClick={() => onPick?.({start: toSeconds(slot.start), end: toSeconds(slot.end)})}>
                            <span className="sa-rank flex align-items-center justify-content-center flex-shrink-0">{digits(index + 1)}</span>
                            <span className="flex-1 min-w-0">
                                <span className="flex flex-wrap align-items-center gap-2">
                                    <strong className="text-sm text-900 ltr">{clock(slot.start)} – {clock(slot.end)}</strong>
                                    {index === 0 && <span className="sa-tag good">بهترین پیشنهاد</span>}
                                </span>
                                <small className="block text-2xs text-500 mt-1">{relativeDay(slot.day, today)} · {minutesText(shownDuration)} آزاد{slot.beforeDeadline ? " · پیش از مهلت" : ""}</small>
                            </span>
                            <span className="sa-pick flex-shrink-0">انتخاب</span>
                        </button>) : <div className="sa-hint text-xs line-height-3 p-3 border-round-lg">
                            در {digits(result.searchedDays || days)} روز بررسی‌شده (تا {dayLabel(result.lastDay)}) زمان آزادی با این مدت پیدا نشد.
                            {days === 7 && <Button type="button" text size="small" className="text-xs p-1 mr-1" label="بررسی ۱۴ روز" onClick={() => setDays(14)}/>}
                        </div>}
                    </section>}
                </>}
            </>}
        </div>
        <footer className="sa-footer text-2xs text-500 line-height-3 p-3">
            <i className="pi pi-info-circle ml-1" aria-hidden="true"/>
            انتخاب یک پیشنهاد فقط تاریخ و ساعت را تنظیم می‌کند و تا زدن «{saveLabel}» چیزی ثبت نمی‌شود.
            {result?.window && !result.window.stored ? ` ساعات کاری افراد در زوفینو ثبت نشده؛ زمان آزاد در بازهٔ ${digits(Number(result.window.from.slice(0, 2)))} تا ${digits(Number(result.window.to.slice(0, 2)))} جست‌وجو می‌شود.` : ""}
        </footer>
    </aside>;
}

// the assignee's day across the search window: busy blocks, the chosen range on top
function DayBar({result, day, selection, loading}) {
    const window = useMemo(() => result?.window || {from: "08:00", to: "20:00"}, [result]);
    const busy = useMemo(() => (result?.dayBusy || []).map((item) => ({item, segment: barSegment(item.start, item.end, day, window)})).filter((row) => row.segment), [result, day, window]);
    const chosen = selection ? barSegment(new Date(selection.start * 1000).toISOString(), new Date(selection.end * 1000).toISOString(), day, window) : null;
    const ticks = [0, 1, 2, 3, 4].map((index) => {
        const from = Number(window.from.slice(0, 2));
        const to = Number(window.to.slice(0, 2));
        return Math.round(from + ((to - from) * index) / 4);
    });
    if (loading) return <Skeleton height="2rem" className="mt-3 border-round"/>;
    return <div className="mt-3">
        <div className="sa-ticks flex justify-content-between text-2xs text-400 mb-1">{ticks.map((hour) => <span key={hour}>{digits(hour)}:۰۰</span>)}</div>
        <div className="sa-bar" role="img" aria-label={`برنامهٔ ${dayLabel(day)}: ${digits(busy.length)} بازهٔ مشغول`}>
            {busy.map(({item, segment}, index) => <span key={index} className="sa-busy" style={{right: `${segment.left}%`, width: `${segment.width}%`}} title={`${item.title || "مشغول"} · ${clock(item.start)} تا ${clock(item.end)}`}/>)}
            {chosen && <span className="sa-chosen" style={{right: `${chosen.left}%`, width: `${chosen.width}%`}}/>}
        </div>
        <div className="flex gap-3 mt-2 text-2xs text-500">
            <span className="flex align-items-center gap-1"><i className="sa-dot busy"/>مشغول</span>
            <span className="flex align-items-center gap-1"><i className="sa-dot free"/>آزاد</span>
            {chosen && <span className="flex align-items-center gap-1"><i className="sa-dot chosen"/>زمان انتخابی</span>}
            {!busy.length && <span className="mr-auto">کاری با زمان مشخص در این روز ثبت نشده</span>}
        </div>
    </div>;
}
